// Leads importieren: CSV wählen (Komma/Semikolon, UTF-8/Latin-1) → Spalten zuordnen → Vorschau mit Dubletten-Prüfung je
// Zeile → Anlage in Close in Stapeln mit Fortschritt → Ergebnisbericht mit Fehlerliste.
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fehlerText } from "../../api";
import { leadImportApi, type ImportErgebnisZeile, type ImportPruefung, type ImportZeile } from "../../api/leadsNeu";
import { useMeta } from "../../daten/meta";
import { Auswahl } from "../Auswahl";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { csvLesen, csvSchreiben, dateiSpeichern, dekodieren, zeileZuordnen, ZIELFELDER, zuordnungVorschlagen, type CsvInhalt, type Zuordnung } from "./csv";
import "./smartview.css";

const MAX_ZEILEN = 5000;
const PRUEF_STAPEL = 200;
const ANLEGE_STAPEL = 20;
const VORSCHAU_ZEILEN = 300;

type Schritt = "datei" | "zuordnung" | "vorschau" | "lauf" | "ergebnis";

interface ZeileStand {
  nummer: number; // Zeile in der Datei (1 = Kopf)
  werte: ImportZeile;
  pruefung: ImportPruefung | null;
  indatei: boolean; // Telefon oder Mail kommt weiter oben in der Datei schon vor
  anlegen: boolean;
}

export function ImportDialog({ offen, onSchliessen }: { offen: boolean; onSchliessen: () => void }) {
  const { meta, nutzerListe } = useMeta();
  const navigate = useNavigate();
  const [schritt, setSchritt] = useState<Schritt>("datei");
  const [csv, setCsv] = useState<CsvInhalt | null>(null);
  const [dateiname, setDateiname] = useState("");
  const [zuordnung, setZuordnung] = useState<Zuordnung>([]);
  const [statusId, setStatusId] = useState<string | null>(null);
  const [bearbeiter, setBearbeiter] = useState<string | null>(null);
  const [zeilen, setZeilen] = useState<ZeileStand[]>([]);
  const [pruefFortschritt, setPruefFortschritt] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fortschritt, setFortschritt] = useState({ fertig: 0, gesamt: 0 });
  const [ergebnisse, setErgebnisse] = useState<ImportErgebnisZeile[]>([]);
  const [abgebrochen, setAbgebrochen] = useState(false);
  const abbruch = useRef(false);

  const leadFelder = useMemo(() => (meta?.custom_fields.lead ?? []).filter((f) => f.type !== "contact" && f.type !== "hidden"), [meta]);
  const statusOptionen = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const bearbeiterFeld = meta?.custom_fields.lead.find((f) => f.type === "user" && f.name.trim().toLowerCase() === "bearbeiter");
  const nutzerOptionen = nutzerListe.filter((u) => u.close_user_id && u.is_active).map((u) => ({ wert: u.close_user_id as string, label: u.name }));

  const zuruecksetzen = useCallback(() => {
    setSchritt("datei");
    setCsv(null);
    setDateiname("");
    setZuordnung([]);
    setZeilen([]);
    setPruefFortschritt(null);
    setFehler(null);
    setErgebnisse([]);
    setAbgebrochen(false);
    setFortschritt({ fertig: 0, gesamt: 0 });
  }, []);

  const schliessen = () => {
    abbruch.current = true;
    onSchliessen();
    window.setTimeout(zuruecksetzen, 200);
  };

  const dateiWaehlen = async (datei: File | undefined) => {
    if (!datei) return;
    setFehler(null);
    try {
      const { text, kodierung } = dekodieren(await datei.arrayBuffer());
      const inhalt = csvLesen(text, kodierung);
      if (!inhalt.kopf.length || !inhalt.zeilen.length) {
        setFehler("In der Datei stehen keine Zeilen. Die erste Zeile muss die Spaltenüberschriften enthalten.");
        return;
      }
      if (inhalt.zeilen.length > MAX_ZEILEN) {
        setFehler(`Die Datei hat ${inhalt.zeilen.length.toLocaleString("de-DE")} Zeilen, erlaubt sind ${MAX_ZEILEN.toLocaleString("de-DE")} je Import. Bitte aufteilen.`);
        return;
      }
      setCsv(inhalt);
      setDateiname(datei.name);
      setZuordnung(zuordnungVorschlagen(inhalt.kopf, leadFelder));
      setSchritt("zuordnung");
    } catch (e) {
      setFehler(`Die Datei ließ sich nicht lesen: ${fehlerText(e)}`);
    }
  };

  const firmaZugeordnet = zuordnung.includes("firma");

  const vorschauBauen = async () => {
    if (!csv) return;
    setFehler(null);
    setSchritt("vorschau");
    const roh: ImportZeile[] = csv.zeilen.map((z) => zeileZuordnen(z, zuordnung));
    const stand: ZeileStand[] = roh.map((werte, i) => ({ nummer: i + 2, werte, pruefung: null, indatei: false, anlegen: false }));
    setZeilen(stand);
    setPruefFortschritt(0);
    try {
      for (let i = 0; i < roh.length; i += PRUEF_STAPEL) {
        const teil = roh.slice(i, i + PRUEF_STAPEL);
        const r = await leadImportApi.pruefen(teil);
        r.data.forEach((p) => {
          stand[i + p.index].pruefung = p;
        });
        setPruefFortschritt(Math.min(roh.length, i + PRUEF_STAPEL));
      }
      // Dubletten innerhalb der Datei: gleiche Nummer oder Mail wie eine Zeile weiter oben
      const gesehen = new Set<string>();
      for (const z of stand) {
        const p = z.pruefung;
        const schluessel = [p?.telefon_e164, p?.email_norm].filter(Boolean) as string[];
        z.indatei = schluessel.some((k) => gesehen.has(k));
        schluessel.forEach((k) => gesehen.add(k));
        z.anlegen = !!p?.gueltig && !p.dubletten.some((d) => d.gruende.some((g) => g.art !== "name")) && !z.indatei;
      }
      setZeilen([...stand]);
      setPruefFortschritt(null);
    } catch (e) {
      setFehler(fehlerText(e));
      setPruefFortschritt(null);
      setSchritt("zuordnung");
    }
  };

  const hartDublette = (z: ZeileStand) => !!z.pruefung?.dubletten.some((d) => d.gruende.some((g) => g.art !== "name")) || z.indatei;
  const zaehl = useMemo(() => {
    let ok = 0;
    let ungueltig = 0;
    let dubletten = 0;
    for (const z of zeilen) {
      if (!z.pruefung?.gueltig) ungueltig++;
      else if (hartDublette(z)) dubletten++;
      else ok++;
    }
    return { ok, ungueltig, dubletten, anlegen: zeilen.filter((z) => z.anlegen).length };
  }, [zeilen]);

  const umschalten = (nummer: number) =>
    setZeilen((l) => l.map((z) => (z.nummer === nummer && z.pruefung?.gueltig ? { ...z, anlegen: !z.anlegen } : z)));
  const dublettenAlleAn = (an: boolean) =>
    setZeilen((l) => l.map((z) => (z.pruefung?.gueltig && hartDublette(z) ? { ...z, anlegen: an } : z)));

  const starten = async () => {
    const zuAnlegen = zeilen.filter((z) => z.anlegen);
    if (!zuAnlegen.length) return;
    abbruch.current = false;
    setAbgebrochen(false);
    setErgebnisse([]);
    setFehler(null);
    setFortschritt({ fertig: 0, gesamt: zuAnlegen.length });
    setSchritt("lauf");
    // Erst die Zeilen ohne Dublette (der Server prüft nochmals), dann die ausdrücklich gewollten Dubletten
    const normale = zuAnlegen.filter((z) => !hartDublette(z));
    const gewollt = zuAnlegen.filter((z) => hartDublette(z));
    const alle: ImportErgebnisZeile[] = [];
    try {
      for (const [gruppe, modus] of [
        [normale, "ueberspringen"],
        [gewollt, "trotzdem"],
      ] as const) {
        for (let i = 0; i < gruppe.length; i += ANLEGE_STAPEL) {
          if (abbruch.current) throw new Error("abgebrochen");
          const teil = gruppe.slice(i, i + ANLEGE_STAPEL);
          const r = await leadImportApi.anlegen({
            zeilen: teil.map((z) => z.werte),
            nummern: teil.map((z) => z.nummer),
            dubletten: modus,
            standard_status_id: statusId,
            bearbeiter_id: bearbeiterFeld ? bearbeiter : null,
          });
          alle.push(...r.data);
          setErgebnisse([...alle]);
          setFortschritt({ fertig: alle.length, gesamt: zuAnlegen.length });
        }
      }
    } catch (e) {
      if ((e as Error).message === "abgebrochen") setAbgebrochen(true);
      else setFehler(`Der Import wurde unterbrochen: ${fehlerText(e)}`);
    }
    setSchritt("ergebnis");
  };

  const angelegt = ergebnisse.filter((e) => e.ergebnis === "angelegt").length;
  const uebersprungen = ergebnisse.filter((e) => e.ergebnis === "uebersprungen");
  const fehlerListe = ergebnisse.filter((e) => e.ergebnis === "fehler");
  const vorabFehler = zeilen.filter((z) => !z.anlegen && z.pruefung && !z.pruefung.gueltig);

  const fehlerlisteSpeichern = () => {
    const zeilenCsv: string[][] = [["Zeile", "Firma", "Ergebnis", "Grund"]];
    vorabFehler.forEach((z) => zeilenCsv.push([String(z.nummer), z.werte.firma ?? "", "nicht geprüft ok", (z.pruefung?.fehler ?? []).join("; ")]));
    [...uebersprungen, ...fehlerListe].forEach((e) =>
      zeilenCsv.push([String(e.zeile), e.firma ?? "", e.ergebnis === "fehler" ? "Fehler" : "übersprungen", e.fehler ?? ""]),
    );
    dateiSpeichern(`import-fehlerliste-${new Date().toISOString().slice(0, 10)}.csv`, csvSchreiben(zeilenCsv));
  };

  const zielOptionen = (
    <>
      <option value="">– nicht übernehmen –</option>
      <optgroup label="Lead und Kontakt">
        {ZIELFELDER.map((z) => (
          <option key={z.key} value={z.key}>
            {z.label}
          </option>
        ))}
      </optgroup>
      {leadFelder.length ? (
        <optgroup label="Felder des Leads">
          {leadFelder.map((f) => (
            <option key={f.id} value={`custom:${f.id}`}>
              {f.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </>
  );

  let fuss: React.ReactNode = null;
  if (schritt === "datei") {
    fuss = (
      <button type="button" className="knopf" onClick={schliessen}>
        Abbrechen
      </button>
    );
  } else if (schritt === "zuordnung") {
    fuss = (
      <>
        <button type="button" className="knopf" onClick={zuruecksetzen}>
          Andere Datei
        </button>
        <button type="button" className="knopf knopf--primaer" disabled={!firmaZugeordnet} title={firmaZugeordnet ? undefined : "Bitte eine Spalte der Firma zuordnen"} onClick={() => void vorschauBauen()}>
          Weiter zur Vorschau
        </button>
      </>
    );
  } else if (schritt === "vorschau") {
    fuss = (
      <>
        <button type="button" className="knopf" onClick={() => setSchritt("zuordnung")} disabled={pruefFortschritt !== null}>
          Zurück
        </button>
        <button type="button" className="knopf knopf--primaer" disabled={pruefFortschritt !== null || zaehl.anlegen === 0} onClick={() => void starten()}>
          {zaehl.anlegen.toLocaleString("de-DE")} {zaehl.anlegen === 1 ? "Lead" : "Leads"} in Close anlegen
        </button>
      </>
    );
  } else if (schritt === "lauf") {
    fuss = (
      <button type="button" className="knopf" onClick={() => (abbruch.current = true)}>
        Abbrechen
      </button>
    );
  } else {
    fuss = (
      <>
        {fehlerListe.length + uebersprungen.length + vorabFehler.length ? (
          <button type="button" className="knopf" onClick={fehlerlisteSpeichern}>
            Fehlerliste als CSV
          </button>
        ) : null}
        <button type="button" className="knopf" onClick={() => { schliessen(); navigate("/leads?sort=-date_created"); }}>
          Zu den Leads
        </button>
        <button type="button" className="knopf knopf--primaer" onClick={schliessen}>
          Fertig
        </button>
      </>
    );
  }

  return (
    <Dialog titel="Leads importieren" offen={offen} onSchliessen={schliessen} breite={schritt === "datei" ? 520 : 780} fuss={fuss}>
      <div className="svd">
        {schritt === "datei" ? (
          <>
            <p className="svd__leise">
              CSV-Datei mit Spaltenüberschriften in der ersten Zeile (Komma oder Semikolon, UTF-8 oder Latin-1). Höchstens {MAX_ZEILEN.toLocaleString("de-DE")} Zeilen je Import.
            </p>
            <input className="eingabe" type="file" accept=".csv,.tsv,.txt,text/csv" onChange={(e) => void dateiWaehlen(e.target.files?.[0])} data-autofocus />
          </>
        ) : null}

        {schritt === "zuordnung" && csv ? (
          <>
            <p className="svd__leise">
              {dateiname}: {csv.zeilen.length.toLocaleString("de-DE")} Zeilen, Trenner „{csv.trenner === "\t" ? "Tab" : csv.trenner}“, {csv.kodierung === "utf-8" ? "UTF-8" : "Latin-1"}. Ordne jeder Spalte ein Feld zu.
            </p>
            <div className="svd__scroll">
              <table className="svd__tabelle">
                <thead>
                  <tr>
                    <th>Spalte in der Datei</th>
                    <th>Beispiel</th>
                    <th>Feld im CRM</th>
                  </tr>
                </thead>
                <tbody>
                  {csv.kopf.map((k, i) => (
                    <tr key={i}>
                      <td>{k || <span className="svd__leise">(ohne Name)</span>}</td>
                      <td className="svd__leise">{(csv.zeilen[0]?.[i] ?? "").slice(0, 40)}</td>
                      <td>
                        <select
                          className="eingabe eingabe--klein"
                          value={zuordnung[i] ?? ""}
                          onChange={(e) => setZuordnung((z) => z.map((w, j) => (j === i ? ((e.target.value || null) as Zuordnung[number]) : w)))}
                        >
                          {zielOptionen}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="svd__zeile">
              <div className="feld">
                <span className="feld__label">Status für Zeilen ohne (erkannten) Status</span>
                <Auswahl optionen={statusOptionen} wert={statusId} leerErlaubt platzhalter="Standard von Close" onWahl={(w) => setStatusId((w as string) || null)} />
              </div>
              {bearbeiterFeld ? (
                <div className="feld">
                  <span className="feld__label">Bearbeiter für alle neuen Leads</span>
                  <Auswahl optionen={nutzerOptionen} wert={bearbeiter} leerErlaubt platzhalter="Niemand" onWahl={(w) => setBearbeiter((w as string) || null)} />
                </div>
              ) : null}
            </div>
            {!firmaZugeordnet ? (
              <div className="hinweis hinweis--warnung">
                <Icon name="warnung" groesse={15} />
                <span>Ohne Spalte für die Firma lässt sich nichts anlegen.</span>
              </div>
            ) : null}
          </>
        ) : null}

        {schritt === "vorschau" ? (
          pruefFortschritt !== null ? (
            <>
              <div>Zeilen werden geprüft … {pruefFortschritt.toLocaleString("de-DE")} von {zeilen.length.toLocaleString("de-DE")}</div>
              <div className="svd__balken">
                <span style={{ width: `${(100 * pruefFortschritt) / Math.max(1, zeilen.length)}%` }} />
              </div>
            </>
          ) : (
            <>
              <div className="svd__pruefung">
                <span>
                  <strong>{zaehl.ok.toLocaleString("de-DE")}</strong> in Ordnung · <strong className={zaehl.dubletten ? "svd__fehler" : undefined}>{zaehl.dubletten.toLocaleString("de-DE")}</strong> Dubletten ·{" "}
                  <strong className={zaehl.ungueltig ? "svd__fehler" : undefined}>{zaehl.ungueltig.toLocaleString("de-DE")}</strong> mit Fehler
                </span>
                {zaehl.dubletten ? (
                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
                    <button type="button" className="textknopf" onClick={() => dublettenAlleAn(false)}>
                      Dubletten überspringen
                    </button>
                    <button type="button" className="textknopf" onClick={() => dublettenAlleAn(true)}>
                      Dubletten trotzdem anlegen
                    </button>
                  </span>
                ) : null}
              </div>
              <div className="svd__scroll">
                <table className="svd__tabelle">
                  <thead>
                    <tr>
                      <th>Anlegen</th>
                      <th>Zeile</th>
                      <th>Firma</th>
                      <th>Kontakt</th>
                      <th>Telefon</th>
                      <th>Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zeilen.slice(0, VORSCHAU_ZEILEN).map((z) => {
                      const p = z.pruefung;
                      const d = p?.dubletten.find((x) => x.gruende.some((g) => g.art !== "name"));
                      const hinweis = !p?.gueltig
                        ? p?.fehler.join("; ")
                        : z.indatei
                          ? "kommt in der Datei weiter oben schon vor"
                          : d
                            ? `Gibt es schon: ${d.name}`
                            : p.warnungen.join("; ");
                      return (
                        <tr key={z.nummer}>
                          <td>
                            <input type="checkbox" checked={z.anlegen} disabled={!p?.gueltig} onChange={() => umschalten(z.nummer)} aria-label={`Zeile ${z.nummer} anlegen`} />
                          </td>
                          <td className="svd__leise">{z.nummer}</td>
                          <td>{z.werte.firma}</td>
                          <td>{z.werte.kontakt_name}</td>
                          <td>{z.werte.telefon}</td>
                          <td className={!p?.gueltig ? "svd__fehler" : d || z.indatei ? undefined : "svd__leise"}>{hinweis}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {zeilen.length > VORSCHAU_ZEILEN ? <div className="svd__leise">Gezeigt werden die ersten {VORSCHAU_ZEILEN} von {zeilen.length.toLocaleString("de-DE")} Zeilen; die übrigen folgen der gleichen Regel.</div> : null}
            </>
          )
        ) : null}

        {schritt === "lauf" ? (
          <>
            <div>
              Leads werden in Close angelegt … {fortschritt.fertig.toLocaleString("de-DE")} von {fortschritt.gesamt.toLocaleString("de-DE")}
            </div>
            <div className="svd__balken">
              <span style={{ width: `${(100 * fortschritt.fertig) / Math.max(1, fortschritt.gesamt)}%` }} />
            </div>
            <div className="svd__leise">Bitte das Fenster offen lassen.</div>
          </>
        ) : null}

        {schritt === "ergebnis" ? (
          <>
            <div className={`hinweis ${fehler ? "hinweis--fehler" : abgebrochen ? "hinweis--warnung" : "hinweis--ok"}`}>
              <Icon name={fehler || abgebrochen ? "warnung" : "haken"} groesse={15} />
              <span>
                {fehler ?? (abgebrochen ? "Der Import wurde abgebrochen. Was bis dahin angelegt war, bleibt bestehen." : "Import abgeschlossen.")}
              </span>
            </div>
            <div className="svd__pruefung">
              <span>
                <strong className="svd__ok">{angelegt.toLocaleString("de-DE")}</strong> angelegt · <strong>{uebersprungen.length.toLocaleString("de-DE")}</strong> übersprungen (Dublette) ·{" "}
                <strong className={fehlerListe.length ? "svd__fehler" : undefined}>{fehlerListe.length.toLocaleString("de-DE")}</strong> Fehler · {vorabFehler.length.toLocaleString("de-DE")} schon in der Vorschau ausgeschlossen
              </span>
            </div>
            {uebersprungen.length + fehlerListe.length ? (
              <div className="svd__scroll">
                <table className="svd__tabelle">
                  <thead>
                    <tr>
                      <th>Zeile</th>
                      <th>Firma</th>
                      <th>Ergebnis</th>
                      <th>Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...uebersprungen, ...fehlerListe].map((e) => (
                      <tr key={`${e.zeile}-${e.index}`}>
                        <td>{e.zeile}</td>
                        <td>{e.firma}</td>
                        <td>{e.ergebnis === "fehler" ? "Fehler" : "übersprungen"}</td>
                        <td className={e.ergebnis === "fehler" ? "svd__fehler" : undefined}>{e.fehler}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}

        {fehler && schritt !== "ergebnis" ? (
          <div className="hinweis hinweis--fehler" role="alert">
            <Icon name="warnung" groesse={15} />
            <span>{fehler}</span>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
